/**
 * SubtitleManager - Unified subtitle management component
 *
 * Handles:
 * - Display of existing subtitles (from Stream/database or R2 staging)
 * - Adding new subtitles (file select → process → upload)
 * - Deleting existing subtitles
 *
 * Supports two modes:
 * - "admin": Upload to Cloudflare Stream (for published titles)
 * - "provider": Upload to R2 staging (for draft submissions)
 */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */

import { authenticatedFetch } from "@lumi/data";
import type { SubtitleProcessingResult } from "@lumi/lib";
import { processSubtitleFile } from "@lumi/lib";
import { AlertTriangle } from "lucide-react";
import { useState, type ReactElement } from "react";
import { toast } from "sonner";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { normalizeLanguageName } from "../../../utils/languageMapping";
import { SubtitleDetailsDialog } from "./SubtitleDetailsDialog";
import type { ExistingSubtitle, PendingSubtitle } from "./SubtitleRow";
import { SubtitleRow } from "./SubtitleRow";
import type { TranslationResult } from "./TranslateSubtitleModal";
import { TranslateSubtitleModal } from "./TranslateSubtitleModal";
import { TranslationPreviewModal } from "./TranslationPreviewModal";

/**
 * Parse a warning message to extract category and cue number
 * Matches the server-side logic in AssetMetadataService
 */
function parseWarning(warning: string): { category: string; cueNumber: number } | null {
  const cueAtLineMatch = /cue at line\s+(\d+)/i.exec(warning);
  const lineMatch = /^line\s+(\d+)/i.exec(warning);
  const number = cueAtLineMatch?.[1] || lineMatch?.[1];

  if (!number) return null;

  const lowerWarning = warning.toLowerCase();
  let category: string | null = null;

  if (lowerWarning.includes("overlap")) {
    category = "Overlapping cues";
  } else if (lowerWarning.includes("gap")) {
    category = "Gap issues";
  } else if (lowerWarning.includes("reading speed") || lowerWarning.includes("chars/sec")) {
    category = "Reading speed";
  } else if (lowerWarning.includes("beyond video duration")) {
    category = "Extends past video";
  } else if (lowerWarning.includes("non-standard tags")) {
    category = "Invalid tags";
  } else if (lowerWarning.includes("unclosed")) {
    category = "Unclosed tags";
  }

  if (!category) return null;
  return { category, cueNumber: Number.parseInt(number, 10) };
}

/**
 * Format grouped warnings into readable summary strings
 */
function formatGroupedWarnings(groups: Record<string, number[]>): string[] {
  const result: string[] = [];
  for (const [category, numbers] of Object.entries(groups)) {
    const sorted = [...new Set(numbers)].sort((a, b) => a - b);
    if (sorted.length > 5) {
      result.push(`${category}: lines ${sorted.slice(0, 5).join(", ")}, +${sorted.length - 5} more`);
    } else {
      result.push(`${category}: lines ${sorted.join(", ")}`);
    }
  }
  return result;
}

/**
 * Group warnings by type with line/cue numbers for compact display
 * e.g., "Gap issues: lines 2, 5, 19" instead of repeating full message
 * Matches the server-side logic in AssetMetadataService.groupWarningsByType()
 */
function groupWarningsByType(warnings: string[]): string[] {
  const groups: Record<string, number[]> = {};
  const ungrouped: string[] = [];
  let readingSpeedCount = 0;

  for (const warning of warnings) {
    // Special handling for reading speed warnings (no line number)
    if (warning.toLowerCase().includes("reading speed") || warning.toLowerCase().includes("chars/sec")) {
      readingSpeedCount++;
      continue;
    }

    const parsed = parseWarning(warning);
    if (parsed) {
      const { category, cueNumber } = parsed;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(cueNumber);
    } else {
      ungrouped.push(warning);
    }
  }

  const result = formatGroupedWarnings(groups);

  // Add reading speed summary if any
  if (readingSpeedCount > 0) {
    result.push(`Reading speed: ${readingSpeedCount} cue${readingSpeedCount > 1 ? "s" : ""} too fast`);
  }

  return [...result, ...ungrouped];
}

/** Provider subtitle from submission metadata (R2 staging) */
export interface ProviderSubtitle {
  language: string;
  label: string;
  fileName: string;
  r2Key: string;
  /** Original format before any conversion */
  format?: "VTT" | "SRT";
  processing?: {
    cueCount: number;
    sizeKB: number;
    wasConverted: boolean;
    wasNormalized: boolean;
    timecodeOffset: number;
  };
  /** Validation results from subtitle processing */
  validation?: {
    valid: boolean;
    errors: string[];
    /** Detailed warnings (individual cue messages) */
    warnings: string[];
    /** Grouped warnings for compact tooltip display */
    warningsSummary?: string[];
  };
}

interface SubtitleManagerProps {
  /** Mode determines upload destination and API routes */
  readonly mode?: "admin" | "provider";
  /** Asset ID for admin API operations (required for admin mode) */
  readonly assetId?: string;
  /** Submission ID for provider API operations (required for provider mode) */
  readonly submissionId?: string;
  /** Asset kind for R2 path (feature/trailer) */
  readonly assetKind: "feature" | "trailer";
  /** Existing subtitles from database (admin mode) */
  readonly existingSubtitles?: ExistingSubtitle[];
  /** Existing subtitles from submission metadata (provider mode) */
  readonly providerSubtitles?: ProviderSubtitle[];
  /** Callback after any subtitle change (add/delete) */
  readonly onRefresh: () => Promise<void>;
  /** Callback when provider subtitles change (provider mode only) */
  readonly onProviderSubtitlesChange?: (subtitles: ProviderSubtitle[]) => void;
  /** Whether to show full processing preview or compact view */
  readonly compact?: boolean;
}

interface PendingUpload extends PendingSubtitle {
  processing: SubtitleProcessingResult;
}

/**
 * Processing preview section for pending uploads
 */
function ProcessingPreview({
  pending,
  onClear,
}: Readonly<{
  pending: PendingUpload;
  onClear: () => void;
}>): ReactElement {
  const { processing } = pending;
  const hasWarnings = processing.validation.warnings.length > 0;

  return (
    <div className="mt-2 p-3 bg-neutral-900/50 border border-neutral-700 rounded text-xs">
      <div className="flex items-center justify-between mb-2">
        <span className="text-neutral-300">
          {processing.cueCount} cues • {processing.sizeKB.toFixed(1)} KB
          {processing.wasConverted && " • Converted from SRT"}
          {processing.wasNormalized && ` • Normalized (shifted ${(processing.timecodeOffset / 1000).toFixed(2)}s)`}
        </span>
        <button type="button" onClick={onClear} className="text-neutral-500 hover:text-neutral-300">
          Clear
        </button>
      </div>

      {hasWarnings && (
        <div className="flex items-start gap-2 text-yellow-400">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <div className="space-y-0.5">
            {processing.validation.warnings.map((warn, idx) => (
              <div key={`warn-${idx}-${warn.length}-${warn.codePointAt(0) ?? 0}`}>{warn}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Convert ProviderSubtitle to ExistingSubtitle format for display
 */
function providerToExisting(sub: ProviderSubtitle): ExistingSubtitle {
  // Build metadata object based on available data
  let metadata: ExistingSubtitle["metadata"] = null;
  if (sub.validation) {
    metadata = {
      validation: {
        valid: sub.validation.valid,
        errors: sub.validation.errors,
        warnings: sub.validation.warnings,
        warningsSummary: sub.validation.warningsSummary,
      },
      processing: sub.processing,
    };
  } else if (sub.processing) {
    metadata = { processing: sub.processing };
  }

  return {
    id: sub.r2Key, // Use r2Key as ID for provider subtitles
    lang: sub.language,
    format: sub.format ?? "VTT",
    url: "", // Not needed for display
    forced: false,
    fileName: sub.fileName,
    metadata,
  };
}

export function SubtitleManager({
  mode = "admin",
  assetId,
  submissionId,
  assetKind,
  existingSubtitles = [],
  providerSubtitles = [],
  onRefresh,
  onProviderSubtitlesChange,
  compact = false,
}: Readonly<SubtitleManagerProps>): ReactElement {
  // Auth context for authenticated API calls
  const { accessToken, refreshAuth } = useAuth();

  // Pending uploads (files selected but not yet uploaded)
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedSubtitle, setSelectedSubtitle] = useState<ExistingSubtitle | null>(null);

  // Translation modal state (admin mode only)
  const [translateModalOpen, setTranslateModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedForTranslation, setSelectedForTranslation] = useState<ExistingSubtitle | null>(null);
  const [translationResult, setTranslationResult] = useState<TranslationResult | null>(null);

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Get display subtitles based on mode
  const displaySubtitles: ExistingSubtitle[] =
    mode === "admin" ? existingSubtitles : providerSubtitles.map(providerToExisting);

  const inputId = `subtitle-file-${assetKind}-${mode === "admin" ? assetId : submissionId}`;

  /**
   * Handle file selection and processing
   */
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProcessingError(null);
    setPendingUpload(null);

    // Validate file extension
    const fileExtRegex = /\.(vtt|srt)$/i;
    if (!fileExtRegex.exec(file.name)) {
      setProcessingError("Please select a .vtt or .srt file");
      return;
    }

    try {
      const content = await file.text();
      const result = processSubtitleFile(content, file.name, {
        sizePercent: 60, // Default size
        validation: {
          maxLinesPerCue: 2,
          maxCharsPerSecond: 21,
          maxLineLength: 42,
          minCueDuration: 792, // 19 frames at 24fps; Ooona min is 20 frames
          maxCueDuration: 7000,
          minGapBetweenCues: 83,
        },
      });

      if (!result.validation.valid) {
        setProcessingError(`Validation failed: ${result.validation.errors.join(", ")}`);
        return;
      }

      // Create pending upload with detected language or default to 'en'
      setPendingUpload({
        fileName: file.name,
        language: "en", // Default, user can change
        warnings: result.validation.warnings,
        processing: result,
      });
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Unknown error";
      setProcessingError(`Processing failed: ${errMsg}`);
    }

    // Clear the input so the same file can be selected again
    e.target.value = "";
  };

  /**
   * Handle language change for pending upload
   */
  const handleLanguageChange = (language: string): void => {
    if (!pendingUpload) return;
    setPendingUpload({ ...pendingUpload, language });
  };

  /**
   * Upload subtitle to Cloudflare Stream (admin mode)
   */
  const uploadToStream = async (): Promise<void> => {
    if (!pendingUpload || !accessToken || !assetId) return;

    const label = normalizeLanguageName(pendingUpload.language);

    await authenticatedFetch(
      `${apiBaseUrl}/v1/admin/assets/${assetId}/upload-subtitle-direct`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vttContent: pendingUpload.processing.vttContent,
          language: pendingUpload.language,
          label,
          isDefault: false,
          processing: {
            wasConverted: pendingUpload.processing.wasConverted,
            wasNormalized: pendingUpload.processing.wasNormalized,
            timecodeOffset: pendingUpload.processing.timecodeOffset,
            cueCount: pendingUpload.processing.cueCount,
            sizeKB: pendingUpload.processing.sizeKB,
            validation: pendingUpload.processing.validation,
          },
        }),
      },
      accessToken,
      createRefreshCallback(refreshAuth),
    );

    toast.success(`Subtitle '${label}' uploaded successfully`);
  };

  /**
   * Upload subtitle to R2 staging (provider mode)
   */
  const uploadToR2 = async (): Promise<ProviderSubtitle> => {
    if (!pendingUpload || !accessToken || !submissionId) {
      throw new Error("Missing required data for upload");
    }

    const label = normalizeLanguageName(pendingUpload.language);
    const vttFileName = pendingUpload.fileName.replace(/\.(srt|vtt)$/i, ".vtt");

    // Get presigned URL using authenticatedFetch with type parameter
    const presignedData = await authenticatedFetch<{ uploadUrl: string; r2Key: string }>(
      `${apiBaseUrl}/v1/provider/upload/subtitle-presigned-url`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId,
          assetKind: assetKind.toUpperCase(),
          language: pendingUpload.language,
          label,
          fileName: vttFileName,
          fileSize: new Blob([pendingUpload.processing.vttContent]).size,
        }),
      },
      accessToken,
      createRefreshCallback(refreshAuth),
    );

    // Upload VTT to R2
    const uploadResponse = await fetch(presignedData.uploadUrl, {
      method: "PUT",
      body: pendingUpload.processing.vttContent,
      headers: { "Content-Type": "text/vtt" },
    });

    if (!uploadResponse.ok) {
      throw new Error("Upload to R2 failed");
    }

    toast.success(`Subtitle '${label}' uploaded successfully`);

    // Build validation object with both detailed warnings and grouped summary
    const rawValidation = pendingUpload.processing.validation;
    const validationWithSummary = rawValidation
      ? {
          valid: rawValidation.valid,
          errors: rawValidation.errors,
          warnings: rawValidation.warnings, // Keep detailed warnings
          warningsSummary: groupWarningsByType(rawValidation.warnings), // Add grouped summary
        }
      : undefined;

    return {
      language: pendingUpload.language,
      label,
      fileName: vttFileName,
      r2Key: presignedData.r2Key,
      format: pendingUpload.processing.wasConverted ? "SRT" : "VTT",
      processing: {
        cueCount: pendingUpload.processing.cueCount,
        sizeKB: pendingUpload.processing.sizeKB,
        wasConverted: pendingUpload.processing.wasConverted,
        wasNormalized: pendingUpload.processing.wasNormalized,
        timecodeOffset: pendingUpload.processing.timecodeOffset,
      },
      validation: validationWithSummary,
    };
  };

  /**
   * Handle upload of pending subtitle
   */
  const handleUpload = async (): Promise<void> => {
    if (!pendingUpload) return;
    if (!accessToken) {
      toast.error("Not authenticated");
      return;
    }

    setUploading(true);
    try {
      if (mode === "admin") {
        await uploadToStream();
        await onRefresh();
      } else {
        const newSubtitle = await uploadToR2();
        // Update provider subtitles list
        const updated = [...providerSubtitles, newSubtitle];
        onProviderSubtitlesChange?.(updated);
        await onRefresh();
      }
      setPendingUpload(null);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Upload failed";
      toast.error(errMsg);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Delete subtitle from Cloudflare Stream (admin mode)
   */
  const deleteFromStream = async (subtitleId: string, language: string): Promise<boolean> => {
    if (!accessToken || !assetId) return false;

    await authenticatedFetch(
      `${apiBaseUrl}/v1/admin/assets/${assetId}/subtitles/${subtitleId}`,
      { method: "DELETE" },
      accessToken,
      createRefreshCallback(refreshAuth),
    );

    toast.success(`Subtitle '${normalizeLanguageName(language)}' deleted`);
    return true;
  };

  /**
   * Delete subtitle from R2 staging (provider mode)
   */
  const deleteFromR2 = async (r2Key: string, language: string): Promise<boolean> => {
    if (!accessToken || !submissionId) return false;

    await authenticatedFetch(
      `${apiBaseUrl}/v1/provider/subtitles`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ submissionId, r2Key }),
      },
      accessToken,
      createRefreshCallback(refreshAuth),
    );

    toast.success(`Subtitle '${normalizeLanguageName(language)}' deleted`);
    return true;
  };

  /**
   * Handle deletion of existing subtitle
   */
  const handleDelete = async (subtitle: ExistingSubtitle): Promise<void> => {
    setDeletingId(subtitle.id);
    setDeleting(true);

    try {
      let success = false;
      if (mode === "admin") {
        success = await deleteFromStream(subtitle.id, subtitle.lang);
        if (success) await onRefresh();
      } else {
        // In provider mode, subtitle.id is the r2Key
        success = await deleteFromR2(subtitle.id, subtitle.lang);
        if (success) {
          // Update provider subtitles list
          const updated = providerSubtitles.filter((s) => s.r2Key !== subtitle.id);
          onProviderSubtitlesChange?.(updated);
          await onRefresh();
        }
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Delete failed";
      toast.error(errMsg);
    } finally {
      setDeletingId(null);
      setDeleting(false);
    }
  };

  /**
   * Clear pending upload
   */
  const handleClearPending = (): void => {
    setPendingUpload(null);
    setProcessingError(null);
  };

  /**
   * View subtitle details in dialog
   */
  const handleViewDetails = (subtitle: ExistingSubtitle): void => {
    setSelectedSubtitle(subtitle);
    setDetailsDialogOpen(true);
  };

  /**
   * Open translate modal for a subtitle (admin mode only)
   */
  const handleTranslate = (subtitle: ExistingSubtitle): void => {
    setSelectedForTranslation(subtitle);
    setTranslateModalOpen(true);
  };

  /**
   * Handle translation completion - opens preview modal
   */
  const handleTranslationComplete = (result: TranslationResult): void => {
    setTranslationResult(result);
    setTranslateModalOpen(false);
    setPreviewModalOpen(true);
  };

  /**
   * Handle successful commit of translation
   */
  const handleCommitSuccess = async (): Promise<void> => {
    toast.success(`${normalizeLanguageName(translationResult?.targetLanguage ?? "")} subtitle added`);
    setPreviewModalOpen(false);
    setTranslationResult(null);
    setSelectedForTranslation(null);
    await onRefresh();
  };

  return (
    <div className="space-y-2">
      {/* Existing subtitles */}
      {displaySubtitles.map((subtitle) => (
        <SubtitleRow
          key={subtitle.id}
          existing={subtitle}
          isLoading={deletingId === subtitle.id || deleting}
          onDelete={() => void handleDelete(subtitle)}
          onViewDetails={handleViewDetails}
          onTranslate={mode === "admin" ? handleTranslate : undefined}
          disabled={deleting || uploading}
        />
      ))}

      {/* Pending upload row */}
      {pendingUpload && (
        <>
          <SubtitleRow
            pending={pendingUpload}
            isLoading={uploading}
            onUpload={() => void handleUpload()}
            onLanguageChange={handleLanguageChange}
            disabled={uploading || deleting}
          />
          {!compact && <ProcessingPreview pending={pendingUpload} onClear={handleClearPending} />}
        </>
      )}

      {/* Empty row for adding new subtitle (only show if no pending) */}
      {!pendingUpload && (
        <SubtitleRow isEmpty inputId={inputId} onFileSelect={(e) => void handleFileSelect(e)} disabled={uploading} />
      )}

      {/* Processing error */}
      {processingError && (
        <div className="p-2 bg-red-900/20 border border-red-700 rounded text-xs text-red-400">{processingError}</div>
      )}

      {/* Subtitle details dialog */}
      <SubtitleDetailsDialog
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        data={
          selectedSubtitle
            ? {
                language: selectedSubtitle.lang,
                format: selectedSubtitle.format,
                fileName: selectedSubtitle.fileName,
                // Admin mode: subtitleId for Stream download
                subtitleId: mode === "admin" ? selectedSubtitle.id : undefined,
                // Provider mode: r2Key + submissionId for R2 download
                r2Key: mode === "provider" ? selectedSubtitle.id : undefined,
                submissionId: mode === "provider" ? submissionId : undefined,
                processing: selectedSubtitle.metadata?.processing,
                validation: selectedSubtitle.metadata?.validation,
              }
            : null
        }
      />

      {/* Translation modals (admin mode only) */}
      {mode === "admin" && (
        <>
          <TranslateSubtitleModal
            open={translateModalOpen}
            onOpenChange={setTranslateModalOpen}
            subtitle={selectedForTranslation}
            onTranslationComplete={handleTranslationComplete}
          />

          <TranslationPreviewModal
            open={previewModalOpen}
            onOpenChange={(open) => {
              setPreviewModalOpen(open);
              if (!open) {
                setTranslationResult(null);
                setSelectedForTranslation(null);
              }
            }}
            subtitleId={selectedForTranslation?.id ?? null}
            result={translationResult}
            onCommitSuccess={() => void handleCommitSuccess()}
          />
        </>
      )}
    </div>
  );
}
